import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";

const CATS = ["All", "Business", "Innovation", "Technology", "Retail", "Property", "Robotics", "Entrepreneurship", "Group News"];

export default function Insights() {
  const [articles, setArticles] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => { api.get("/articles").then(({ data }) => setArticles(data)).catch(() => {}); }, []);

  const filtered = cat === "All" ? articles : articles.filter((a) => a.category === cat);
  const [featured, ...rest] = filtered;

  return (
    <div data-testid="page-insights" className="bg-bd-ivory min-h-screen">
      <section className="pt-40 pb-16 bg-bd-black grain relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline text-bd-gold mb-6">Insights</p>
            <h1 className="font-serif text-4xl md:text-7xl text-bd-ivory leading-[1.05] max-w-4xl">
              Perspectives on building, growth and legacy
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 mb-14">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`insight-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors duration-300 ${
                  cat === c ? "border-bd-gold text-bd-gold" : "border-black/15 text-black/50 hover:border-black/40 hover:text-black"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {filtered.length === 0 && <p className="text-black/40 py-16 text-center">No articles published in this category yet.</p>}

          {featured && (
            <Reveal>
              <Link to={`/insights/${featured.slug}`} data-testid={`insight-featured-${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20 items-center">
                <div className="relative aspect-[16/10] overflow-hidden bg-bd-charcoal">
                  {featured.cover_url ? (
                    <img src={featured.cover_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><span className="font-serif text-7xl text-bd-gold/20">BD</span></div>
                  )}
                </div>
                <div>
                  <p className="overline text-bd-gold mb-4">{featured.category}</p>
                  <h2 className="font-serif text-3xl md:text-5xl text-bd-black leading-tight group-hover:text-bd-gold transition-colors duration-300">{featured.title}</h2>
                  <p className="mt-5 text-base text-black/60 leading-relaxed">{featured.excerpt}</p>
                  <span className="mt-6 inline-block text-xs tracking-[0.18em] uppercase text-bd-gold">Read article →</span>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {rest.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.06}>
                <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group block">
                  <div className="relative aspect-[16/11] overflow-hidden bg-bd-charcoal mb-5">
                    {a.cover_url ? (
                      <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><span className="font-serif text-5xl text-bd-gold/20">BD</span></div>
                    )}
                  </div>
                  <p className="overline text-bd-gold/70 mb-2 text-[0.6rem]">{a.category}</p>
                  <h3 className="font-serif text-2xl text-bd-black leading-tight group-hover:text-bd-gold transition-colors duration-300">{a.title}</h3>
                  <p className="mt-3 text-sm text-black/55 leading-relaxed line-clamp-2">{a.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
